import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Zap, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MultiBrandSelect from "@/components/common/MultiBrandSelect";
import DatePicker from "@/components/common/DatePicker";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { DEFAULT_OPS_RANGE } from "@/lib/dateDefaults";
import { useAuth } from "@/lib/auth";

const fmt = (n) => (n || 0).toLocaleString();

/**
 * Lite Finance Report — raw call activity WITHOUT credit/deposit numbers.
 * Reads directly from ops_call_rows (skipping the pre-aggregated rollup)
 * so numbers are always current — including during an in-flight upload,
 * long before the slow MySQL deposit enrichment finishes. Safe for ops:
 * contains ZERO financial fields.
 */
export default function LiteReportPage() {
  const { user } = useAuth();
  const lockedTeamId = !(user?.is_admin || user?.is_super_admin) && user?.team_id ? user.team_id : null;
  const _range = DEFAULT_OPS_RANGE();

  const [ctx, setCtx] = useState({ brands: [], teams: [], campaigns: [] });
  const [brandIds, setBrandIds] = useState([]);
  const [teamId, setTeamId] = useState(lockedTeamId || "all");
  const [campaignId, setCampaignId] = useState("all");
  const [dateFrom, setDateFrom] = useState(_range.from);
  const [dateTo, setDateTo] = useState(_range.to);
  const [groupBy, setGroupBy] = useState("agent");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ops/my-context").then((r) => setCtx(r.data)).catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { group_by: groupBy };
    if (brandIds.length === 1) p.brand_id = brandIds[0];
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (teamId !== "all") p.team_id = teamId;
    if (campaignId !== "all") p.campaign_id = campaignId;
    return p;
  }, [brandIds, teamId, campaignId, dateFrom, dateTo, groupBy]);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/analytics/lite-report", { params })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [params]);

  // Totals row (recompute rate cols from summed numerator/denominator)
  const totals = useMemo(() => {
    if (!rows.length) return null;
    const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    const total_calls = sum("total_calls");
    const contactable = sum("contactable");
    const wrong_number = sum("wrong_number");
    const interested = sum("interested");
    return {
      total_calls,
      contactable,
      contactable_pct: total_calls ? (contactable / total_calls) * 100 : 0,
      fresh: sum("fresh"),
      followup: sum("followup"),
      wrong_number,
      wrong_number_pct: total_calls ? (wrong_number / total_calls) * 100 : 0,
      invalid_number: sum("invalid_number"),
      interested,
      interested_pct: total_calls ? (interested / total_calls) * 100 : 0,
      unique_customers: sum("unique_customers"),
    };
  }, [rows]);

  const rowLabel = (r) => {
    if (groupBy === "team") return r.team_name || "—";
    if (groupBy === "campaign") return `${r.campaign_name} · ${r.brand_name}`;
    if (groupBy === "date") return r.attempt_date_ist || "—";
    return r.agent_name || "—";
  };

  return (
    <div className="space-y-6" data-testid="lite-report-page">
      <div>
        <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight flex items-center gap-3">
          <Zap className="w-8 h-8 text-amber-500" />
          Lite finance report
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Raw call activity — reads directly from uploaded rows without waiting for the
          slow MySQL deposit enrichment. Shows immediately after upload.
          <span className="ml-1 text-slate-400">No credit or financial numbers here — check the main Ops Dashboard for those.</span>
        </p>
      </div>

      <div className="glass-card p-3 text-xs text-slate-600 flex items-start gap-2 bg-amber-50/40 border-l-4 border-amber-300">
        <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <div>
          <b>Why this exists:</b> the Ops Dashboard's KPIs depend on the deposit-enrichment phase,
          which can take 15-30 minutes on a 300k-row upload. This page bypasses that entirely and
          shows live call activity from the raw rows the moment they're inserted — so team leads
          can see today's dialling volume without waiting.
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 flex gap-3 flex-wrap items-end" data-testid="lite-filter-row">
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Brand</Label>
          <MultiBrandSelect brands={ctx.brands} value={brandIds} onChange={setBrandIds} testId="lite-brand-multi" />
        </div>
        {(ctx.teams?.length > 0) && (
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Team</Label>
            {lockedTeamId ? (
              <div className="glass-input h-9 w-[180px] px-3 flex items-center text-sm text-slate-600 bg-slate-100/70">
                {(ctx.teams.find((t) => t.id === lockedTeamId) || {}).name || "Your team"}
              </div>
            ) : (
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger className="glass-input h-9 w-[180px]" data-testid="lite-team-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  {(ctx.teams || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        {(ctx.campaigns?.length > 0) && (
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="glass-input h-9 w-[180px]" data-testid="lite-campaign-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campaigns</SelectItem>
                {(ctx.campaigns || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">From</Label>
          <DatePicker value={dateFrom} onChange={setDateFrom} testId="lite-date-from" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">To</Label>
          <DatePicker value={dateTo} onChange={setDateTo} testId="lite-date-to" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Group by</Label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="glass-input h-9 w-[140px]" data-testid="lite-group-by">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="agent">Agent</SelectItem>
              <SelectItem value="team">Team</SelectItem>
              <SelectItem value="campaign">Campaign</SelectItem>
              <SelectItem value="date">Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <LoadingOverlay loading={loading}>
        <div className="glass-card p-4" data-testid="lite-report-table-card">
          {rows.length === 0 && !loading ? (
            <div className="text-sm text-slate-500 italic py-8 text-center">
              No call activity in the current window.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lite-report-table">
                <thead>
                  <tr className="text-slate-500 text-xs uppercase">
                    <th className="text-left py-2 px-2">
                      {groupBy === "team" ? "Team" : groupBy === "campaign" ? "Campaign" : groupBy === "date" ? "Date" : "Agent"}
                    </th>
                    <th className="text-right py-2 px-2">Calls</th>
                    <th className="text-right py-2 px-2">Contactable</th>
                    <th className="text-right py-2 px-2" title="Contactable ÷ Calls">Contact %</th>
                    <th className="text-right py-2 px-2">Unique customers</th>
                    <th className="text-right py-2 px-2">Fresh</th>
                    <th className="text-right py-2 px-2">Follow-up</th>
                    <th className="text-right py-2 px-2">Interested</th>
                    <th className="text-right py-2 px-2">Wrong #</th>
                    <th className="text-right py-2 px-2">WN %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/60" data-testid={`lite-row-${i}`}>
                      <td className="py-2 px-2 font-medium">{rowLabel(r)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.total_calls)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.contactable)}</td>
                      <td className="py-2 px-2 text-right">{(r.contactable_pct ?? 0).toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right">{fmt(r.unique_customers)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.fresh)}</td>
                      <td className="py-2 px-2 text-right">{fmt(r.followup)}</td>
                      <td className="py-2 px-2 text-right text-emerald-700">{fmt(r.interested)} <span className="text-slate-400 text-[10px]">({(r.interested_pct ?? 0).toFixed(1)}%)</span></td>
                      <td className="py-2 px-2 text-right">{fmt(r.wrong_number)}</td>
                      <td className={`py-2 px-2 text-right ${(r.wrong_number_pct ?? 0) > 30 ? "text-red-600 font-semibold" : ""}`}>{(r.wrong_number_pct ?? 0).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="lite-report-totals">
                      <td className="py-2 px-2">Total</td>
                      <td className="py-2 px-2 text-right">{fmt(totals.total_calls)}</td>
                      <td className="py-2 px-2 text-right">{fmt(totals.contactable)}</td>
                      <td className="py-2 px-2 text-right">{totals.contactable_pct.toFixed(1)}%</td>
                      <td className="py-2 px-2 text-right">{fmt(totals.unique_customers)}</td>
                      <td className="py-2 px-2 text-right">{fmt(totals.fresh)}</td>
                      <td className="py-2 px-2 text-right">{fmt(totals.followup)}</td>
                      <td className="py-2 px-2 text-right text-emerald-700">{fmt(totals.interested)} <span className="text-slate-400 text-[10px]">({totals.interested_pct.toFixed(1)}%)</span></td>
                      <td className="py-2 px-2 text-right">{fmt(totals.wrong_number)}</td>
                      <td className="py-2 px-2 text-right">{totals.wrong_number_pct.toFixed(1)}%</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </LoadingOverlay>
    </div>
  );
}
